
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "step-1-display-the-apply-with-seek-button"
    }}>{`Step 1: Display the Apply with SEEK button`}</h1>
    <p>{`We strongly recommend that your software models its apply form in the following manner:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`If the `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter is present,
skip ahead to handle authorization in the `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek/handle-authorization#allow-access"
          }}>{`Allow access`}</a>{` scenario.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If the `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter is not present,
display the Apply with SEEK button with a `}<inlineCode parentName="p">{`redirectUri`}</inlineCode>{` back to the current page.`}</p>
      </li>
    </ul>
    <p>{`This simplified model enables your software to seamlessly handle all scenarios described below with minimal effort.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.38e79c68021324499bc4d9bc30234fcb21856a38.mmd.svg')} title="" />
    <h2 {...{
      "id": "pre-fill-scenarios"
    }}>{`Pre-fill scenarios`}</h2>
    <p>{`Apply with SEEK can be used by a candidate to pre-fill in three scenarios:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`When a candidate links out from a SEEK job ad to your apply form,
and they manually click the Apply with SEEK button on the form.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate links out from a SEEK job ad to your apply form,
and they have pre-authorized your software to pre-fill the form.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate arrives on your apply form directly or through another channel,
and they manually click the Apply with SEEK button on the form.`}</p>
      </li>
    </ol>
    <h3 {...{
      "id": "scenario-1-candidate-arrives-from-seek-without-pre-authorization"
    }}>{`Scenario 1: Candidate arrives from SEEK without pre-authorization`}</h3>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.1a8104b5be861b47ae94fa1b9df755e62a3d90d9.mmd.svg')} title="Full sequence" />
    <p>{`If the candidate has `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`linked out`}</a>{` from a SEEK job ad,
you will receive a `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter appended to the query string of the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationMethod/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://www.example.com/job/123445/?seek-token=93WyyF1h6eaeb58ccc384b0e8353daa3011f1ece
`}</code></pre>
    <p>{`The token should be propagated in your software to the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`,
and for subsequent `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/application-complete-signal"
      }}>{`signals`}</a>{` like apply form submission.
It is unique to that specific candidate, job ad, and session,
and should not be reused outside of the context of their current application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":80,\"column\":1,\"offset\":3018},\"end\":{\"line\":90,\"column\":4,\"offset\":3177},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":92,\"column\":1,\"offset\":3179},\"end\":{\"line\":107,\"column\":4,\"offset\":3844},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":109,\"column\":1,\"offset\":3846},\"end\":{\"line\":119,\"column\":4,\"offset\":4130},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":80,\"column\":1,\"offset\":3018},\"end\":{\"line\":90,\"column\":4,\"offset\":3177},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":92,\"column\":1,\"offset\":3179},\"end\":{\"line\":107,\"column\":4,\"offset\":3844},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":109,\"column\":1,\"offset\":3846},\"end\":{\"line\":119,\"column\":4,\"offset\":4130},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <h3 {...{
      "id": "scenario-2-candidate-arrives-from-seek-with-pre-authorization"
    }}>{`Scenario 2: Candidate arrives from SEEK with pre-authorization`}</h3>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.1d4db9b654ab1feec2a5b5ee5f06e8aeb64ddede.mmd.svg')} title="Full sequence" />
    <p>{`If your apply page receives a `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter in the query string,
skip ahead to handle authorization in the `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/handle-authorization#allow-access"
      }}>{`Allow access`}</a>{` scenario.`}</p>
    <p>{`This is a variation of Scenario 1 where the candidate has `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`linked out`}</a>{` from a SEEK job ad,
but they have additionally pre-authorized your software to pre-fill the form using their SEEK Profile.
You may test this scenario by posting a job ad using your `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`test hirer account`}</a>{` with a position title that contains the string `}<inlineCode parentName="p">{`[PREAUTH]`}</inlineCode>{`.`}</p>
    <p>{`As with Scenario 1,
you should propagate the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter for subsequent `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/application-complete-signal"
      }}>{`signals`}</a>{` like apply form submission.`}</p>
    <h3 {...{
      "id": "scenario-3-candidate-arrives-from-another-source"
    }}>{`Scenario 3: Candidate arrives from another source`}</h3>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.ed82c3aa864bb663d2beb63a927739df62384539.mmd.svg')} title="Full sequence" />
    <p>{`Where a candidate has arrived at your apply form from another source
(direct link, company career site, etc.),
the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter will not be present in the query string and should be omitted from the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":167,\"column\":1,\"offset\":5875},\"end\":{\"line\":177,\"column\":4,\"offset\":6034},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The HTTPS URL of your apply form for the specific position.\\n    \\\"applicationUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // An optional hirer-provided opaque job reference.\\n    \\\"seekHirerJobReference\\\": \\\"REQ-65558\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":6036},\"end\":{\"line\":197,\"column\":4,\"offset\":6771},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":199,\"column\":1,\"offset\":6773},\"end\":{\"line\":209,\"column\":4,\"offset\":7057},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":167,\"column\":1,\"offset\":5875},\"end\":{\"line\":177,\"column\":4,\"offset\":6034},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "your": true,
        "apply": true,
        "form": true,
        "specific": true,
        "\\\"applicationUri\\\":": true,
        "An": true,
        "optional": true,
        "hirer-provided": true,
        "opaque": true,
        "job": true,
        "reference.\\n": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"REQ-65558\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":6036},\"end\":{\"line\":197,\"column\":4,\"offset\":6771},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":199,\"column\":1,\"offset\":6773},\"end\":{\"line\":209,\"column\":4,\"offset\":7057},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <h2 {...{
      "id": "render-the-button"
    }}>{`Render the button`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` should be called each time your apply form is loaded without a `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter in the query string.
It provides display assets for the Apply with SEEK button and a one-time use authorization URL that it should link to.
The authorization page prompts the candidate to share their SEEK Profile for the purpose of pre-filling your apply form.`}</p>
    <h3 {...{
      "id": "option-1-seek-button"
    }}>{`Option 1: SEEK button`}</h3>
    <p>{`For a simple out-of-the-box option,
take SEEK’s branded button by selecting one of the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/images"
      }}><inlineCode parentName="a">{`images`}</inlineCode></a>{`.
You should use `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/buttonLabel"
      }}><inlineCode parentName="a">{`buttonLabel`}</inlineCode></a>{` as an accessible `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#basic_buttons"
      }}><inlineCode parentName="a">{`aria-label`}</inlineCode></a>{` and link the button to the supplied `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/url"
      }}><inlineCode parentName="a">{`url`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":228,\"column\":1,\"offset\":8117},\"end\":{\"line\":238,\"column\":4,\"offset\":8276},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"<Partner Name> on behalf of <Hirer Name>\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":240,\"column\":1,\"offset\":8278},\"end\":{\"line\":255,\"column\":4,\"offset\":8939},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":257,\"column\":1,\"offset\":8941},\"end\":{\"line\":267,\"column\":4,\"offset\":9225},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":228,\"column\":1,\"offset\":8117},\"end\":{\"line\":238,\"column\":4,\"offset\":8276},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"<Partner": true,
        "Name>": true,
        "on": true,
        "behalf": true,
        "of": true,
        "<Hirer": true,
        "Name>\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":240,\"column\":1,\"offset\":8278},\"end\":{\"line\":255,\"column\":4,\"offset\":8939},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":257,\"column\":1,\"offset\":8941},\"end\":{\"line\":267,\"column\":4,\"offset\":9225},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <h3 {...{
      "id": "option-2-custom-button"
    }}>{`Option 2: Custom button`}</h3>
    <p>{`If you would rather build the Apply with SEEK button using your own design system,
apply the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/buttonLabel"
      }}><inlineCode parentName="a">{`buttonLabel`}</inlineCode></a>{` to a custom button element and link it to the supplied `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/url"
      }}><inlineCode parentName="a">{`url`}</inlineCode></a>{`.`}</p>
    <p>{`Make sure to exclude the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/images"
      }}><inlineCode parentName="a">{`images`}</inlineCode></a>{` field from your query.
Your software should `}<a parentName="p" {...{
        "href": "/graphql/in-practice#selecting-fields"
      }}>{`only select the fields it uses`}</a>{` as a best practice to reduce the surface area of your integration.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":279,\"column\":1,\"offset\":9684},\"end\":{\"line\":286,\"column\":4,\"offset\":9811},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":288,\"column\":1,\"offset\":9813},\"end\":{\"line\":303,\"column\":4,\"offset\":10470},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":305,\"column\":1,\"offset\":10472},\"end\":{\"line\":312,\"column\":4,\"offset\":10648},\"indent\":[1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":279,\"column\":1,\"offset\":9684},\"end\":{\"line\":286,\"column\":4,\"offset\":9811},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":288,\"column\":1,\"offset\":9813},\"end\":{\"line\":303,\"column\":4,\"offset\":10470},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":305,\"column\":1,\"offset\":10472},\"end\":{\"line\":312,\"column\":4,\"offset\":10648},\"indent\":[1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    url
  }
}
`}</code></pre>
    <h2 {...{
      "id": "content-localisation"
    }}>{`Content localisation`}</h2>
    <p>{`Your software should include the standard `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language"
      }}><inlineCode parentName="a">{`Accept-Language`}</inlineCode></a>{` HTTP request header when it executes the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.
We support multiple brands and languages across our markets,
so providing accurate country information and language preferences enables us to provide a more familiar experience for the candidate.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/graphql/in-practice#content-localisation"
      }}>{`content localisation`}</a>{` for more information.`}</p>
    <h2 {...{
      "id": "content-security-policy-compatibility"
    }}>{`Content Security Policy compatibility`}</h2>
    <p>{`This section is for integration partners who enforce strict content security policies in their web-based software.`}</p>
    <p><a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP"
      }}>{`Content Security Policy`}</a>{` (CSP) is a security feature that helps prevent various types of attacks, including Cross-Site Scripting (XSS) and data injection attacks.
Add the following directive to your CSP to load Apply with SEEK assets from the SEEK API:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`Content-Security-Policy: img-src 'self'
https://integration.seek.com/awsk/button-images/;
`}</code></pre>
    <p>{`This sample directive instructs the browser to only load images from your own domain (`}<inlineCode parentName="p">{`self`}</inlineCode>{`) and our `}<inlineCode parentName="p">{`images`}</inlineCode>{` directory.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;